import { useEffect, useState } from "react";
import {
  CurrencyDollarIcon,
  ClockIcon,
  TruckIcon,
  CubeIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { fetchReportSummary } from "../../../../api/service/report";

/**
 * Loads `GET /service/reports/:id/summary` when `reportId` is a finite number.
 * Pass `refreshKey` (e.g. `report.updated_at`) so the summary refetches after report saves.
 */
export function useServiceReportFinancialSummary(reportId, refreshKey) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = reportId != null ? Number(reportId) : NaN;
    if (!Number.isFinite(id)) {
      setSummary(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchReportSummary(id)
      .then((res) => {
        if (!cancelled) setSummary(res.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportId, refreshKey]);

  return { summary, loading };
}

export const ReportSummarySection = ({
  report,
  summary,
  summaryLoading = false,
}) => {
  if (summaryLoading || !summary) {
    return null;
  }

  // Don't show if report has orders (summary will be empty)
  if (summary.has_orders) {
    return null;
  }

  // Show message if cost is included in project
  if (report?.cost_included_in_project) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Økonomisk oversikt
          </h3>
        </div>
        <div className="px-4 py-5 sm:px-6">
          <div className="rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800">
                  Kostnader og inntekter er inkludert i prosjekt
                </p>
                <p className="mt-1 text-sm text-blue-700">
                  Denne rapportens kostnader og inntekter er inkludert i prosjektsummeringen og vises ikke separat her.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("nb-NO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Økonomisk oversikt
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {summary.invoiced 
            ? "Kostnader og faktisk inntekt for oppdraget" 
            : "Kostnader og forventet inntekt for oppdraget"}
        </p>
      </div>

      <div className="px-4 py-5 sm:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Costs Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <CurrencyDollarIcon className="h-5 w-5 text-red-500" />
              <h4 className="text-md font-semibold text-gray-900">Kostnader</h4>
            </div>

            <div className="space-y-3 pl-7">
              {summary.invoiced ? (
                /* Show order items costs for invoiced reports */
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CubeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Ordrelinjer (kostpris)
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.costs?.order_items || 0)}
                    </span>
                  </div>
                </>
              ) : (
                /* Show expected costs breakdown for non-invoiced reports */
                <>
                  {/* Item Consumption Cost */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CubeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Vareforbruk</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.costs?.item_consumption || 0)}
                    </span>
                  </div>

                  {/* Hour Cost */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Timekost</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.costs?.hours || 0)}
                    </span>
                  </div>

                  {/* Car Allowance Cost */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <TruckIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Bilgodtgjørelse</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.costs?.car_allowance || 0)}
                    </span>
                  </div>
                </>
              )}

              {/* Total Costs */}
              <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                <span className="text-sm font-semibold text-gray-900">
                  Totalt kostnader
                </span>
                <span className="text-sm font-bold text-red-600">
                  {formatCurrency(summary.costs?.total || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Income Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-5 w-5 text-green-500" />
              <h4 className="text-md font-semibold text-gray-900">
                {summary.invoiced ? "Faktisk inntekt" : "Forventet inntekt"}
              </h4>
            </div>

            <div className="space-y-3 pl-7">
              {summary.invoiced ? (
                /* Show order items income for invoiced reports */
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CubeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Ordrelinjer (salgspris)
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.income?.order_items || 0)}
                    </span>
                  </div>
                </>
              ) : (
                /* Show expected income breakdown for non-invoiced reports */
                <>
                  {/* Item Consumption Income */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CubeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Salgspris varer (eks. rabatt)
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.income?.item_consumption || 0)}
                    </span>
                  </div>

                  {/* Hour Income */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Ordinær timespris
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.income?.hours || 0)}
                    </span>
                  </div>

                  {/* Car Allowance Income */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <TruckIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Bilgodtgjørelse</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(summary.income?.car_allowance || 0)}
                    </span>
                  </div>

                  {/* Agreed Price Income */}
                  {summary.income?.agreed_price > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Avtalt pris
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(summary.income?.agreed_price || 0)}
                      </span>
                    </div>
                  )}

                  {/* Additional Hours Income */}
                  {summary.income?.additional_hours > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Tilleggsarbeid (timer)
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(summary.income?.additional_hours || 0)}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Total Income */}
              <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                <span className="text-sm font-semibold text-gray-900">
                  Totalt inntekt
                </span>
                <span className="text-sm font-bold text-green-600">
                  {formatCurrency(summary.income?.total || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 border-t border-gray-200 pt-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.profit || 0)}
            </div>
            <div className="text-sm text-gray-500">Fortjeneste</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {summary.profit_margin || 0}%
            </div>
            <div className="text-sm text-gray-500">Fortjenestemargin</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {formatNumber(summary.hours?.total || summary.total_hours || summary.hours?.registered || 0)}t
            </div>
            <div className="text-sm text-gray-500">Totalt timer</div>
          </div>
        </div>

        {/* Additional Info */}
        {(summary.kilometers > 0 || summary.items_count > 0) && (
          <div className="mt-4 text-xs text-gray-500 space-y-1">
            {summary.kilometers > 0 && (
              <div>Kjørelengde: {formatNumber(summary.kilometers)} km</div>
            )}
            {summary.items_count > 0 && (
              <div>Antall varer: {summary.items_count}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

